import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { getApparelOptionsForProduct, getUkShoeOptionsForProduct } from '../constants/productSizes';
import './OrderPlacement.css';

const OrderPlacement = ({
  product,
  kitDesign,
  onBack,
  onOrderSuccess,
  user,
}) => {
  const isKitOrder = Boolean(kitDesign);
  const isShoes = !isKitOrder && product.category === 'Shoes';
  const apparelSizeOptions = useMemo(() => getApparelOptionsForProduct(), []);
  const ukShoeSizeOptions = useMemo(() => getUkShoeOptionsForProduct(product), [product]);
  const [previewImage, setPreviewImage] = useState(
    () =>
      kitDesign?.compositePreviewBase64 ||
      kitDesign?.screenshotBase64 ||
      product.image
  );
  const [backPreviewImage, setBackPreviewImage] = useState(product.back_image || product.image);
  const [formData, setFormData] = useState({
    fullName: user?.user_metadata?.full_name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    zipCode: '',
    size: 'M',
    quantity: 1,
    description: '',
    useCustomSize: false,
    customSize: {
      chest: '',
      length: '',
      shoulder: '',
      sleeve: ''
    }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success] = useState(false);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => {
    if (kitDesign?.compositePreviewBase64 || kitDesign?.screenshotBase64) {
      setPreviewImage(
        kitDesign.compositePreviewBase64 || kitDesign.screenshotBase64
      );
    } else {
      setPreviewImage(product.image);
    }
    setBackPreviewImage(product.back_image || product.image);
  }, [product, kitDesign]);

  useEffect(() => {
    if (isShoes) {
      const opts = ukShoeSizeOptions;
      const first = opts[0]?.value || 'UK 8';
      setFormData((prev) => ({
        ...prev,
        useCustomSize: false,
        size: opts.some((o) => o.value === prev.size) ? prev.size : first
      }));
    } else {
      const opts = apparelSizeOptions;
      const first = opts[0]?.value || 'M';
      setFormData((prev) => ({
        ...prev,
        size: opts.some((o) => o.value === prev.size) ? prev.size : first
      }));
    }
  }, [isShoes, ukShoeSizeOptions, apparelSizeOptions]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleCustomSizeChange = (e) => {
    setFormData({
      ...formData,
      customSize: {
        ...formData.customSize,
        [e.target.name]: e.target.value
      }
    });
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    setAppliedCoupon(null);
    try {
      const { data, error } = await supabase
        .from('coupons')
        .select('*')
        .eq('code', couponCode.trim().toUpperCase())
        .eq('is_active', true)
        .single();
      if (error || !data) { setCouponError('Invalid or inactive coupon code.'); return; }
      if (data.expiry_date && new Date(data.expiry_date) < new Date()) { setCouponError('This coupon has expired.'); return; }
      if (data.usage_limit !== null && data.times_used >= data.usage_limit) { setCouponError('This coupon has reached its usage limit.'); return; }
      setAppliedCoupon(data);
      setCouponError('');
    } catch (err) {
      setCouponError('Could not validate coupon. Please try again.');
    } finally {
      setCouponLoading(false);
    }
  };

  const calculateTotal = () => {
    let basePrice = parseFloat(product.price.replace('£', '').replace('$', ''));
    if (isKitOrder && kitDesign?.kitOptions?.fullSleeves) basePrice += 2;
    let total = basePrice * formData.quantity;
    if (appliedCoupon) total = total * (1 - appliedCoupon.discount_percent / 100);
    return total.toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const basePrice = parseFloat(product.price.replace('£', '').replace('$', '')) + (isKitOrder && kitDesign?.kitOptions?.fullSleeves ? 2 : 0);
      const customizationKit = isKitOrder
        ? { source: 'kitDesigner3d', kitDesign }
        : null;

      const buildOrderRow = (customizationVal, previewMain, previewBack) => ({
        user_id: user?.id || null,
        customer_name: formData.fullName,
        customer_email: formData.email,
        customer_phone: formData.phone,
        shipping_address: `${formData.address}, ${formData.city}, ${formData.zipCode}`,
        product_id: product.id || null,
        product_name: product.name,
        product_image: product.image,
        product_price: basePrice,
        size: formData.useCustomSize ? 'Custom' : formData.size,
        custom_size: formData.useCustomSize ? formData.customSize : null,
        quantity: parseInt(formData.quantity, 10),
        amount: parseFloat(calculateTotal()),
        status: 'Pending',
        description: formData.description,
        preview_image: previewMain,
        back_preview_image: previewBack,
        customization: customizationVal,
      });

      let customization = customizationKit;
      let previewMain = isKitOrder
        ? kitDesign?.compositePreviewBase64 ||
          kitDesign?.screenshotBase64 ||
          previewImage
        : previewImage;
      let previewBack = backPreviewImage;

      let { error: insertError } = await supabase
        .from('orders')
        .insert([buildOrderRow(customization, previewMain, previewBack)])
        .select();

      if (insertError && isKitOrder) {
        const slimKit = {
          ...kitDesign,
          screenshotBase64: undefined,
          compositePreviewBase64: undefined,
          viewImages: undefined,
        };
        customization = {
          source: 'kitDesigner3d',
          kitDesign: slimKit,
          note: 'Heavy previews omitted (size limit); design data kept',
        };
        previewMain = product.image;
        ({ error: insertError } = await supabase
          .from('orders')
          .insert([buildOrderRow(customization, previewMain, previewBack)])
          .select());
      }

      if (insertError) throw insertError;

      // Increment coupon usage if one was applied
      if (appliedCoupon) {
        await supabase
          .from('coupons')
          .update({ times_used: appliedCoupon.times_used + 1 })
          .eq('id', appliedCoupon.id);
      }

      // Save order details for post-payment email
      const kitOpts = kitDesign?.kitOptions;
      const orderNum = String(Date.now()).slice(-6);
      const message = [
        `Order from: ${formData.fullName}`,
        `Email: ${formData.email}`,
        `Phone: ${formData.phone}`,
        `Address: ${formData.address}, ${formData.city}, ${formData.zipCode}`,
        ``,
        `Product: ${product.name}`,
        `Size: ${formData.size}`,
        `Quantity: ${formData.quantity}`,
        `Total: £${calculateTotal()}`,
        kitOpts?.fullSleeves ? `Full Sleeves: Yes (+£2)` : '',
        kitOpts?.shirtColor ? `Custom Shirt Color: Yes` : '',
        formData.description ? `Notes: ${formData.description}` : '',
      ].filter(Boolean).join('\n');

      localStorage.setItem('pendingOrderEmail', JSON.stringify({
        fullName: formData.fullName,
        email: formData.email,
        orderNum,
        message,
      }));

      // Redirect to Stripe Checkout
      const total = parseFloat(calculateTotal());
      const fnUrl = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL;
      const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      const res = await fetch(`${fnUrl}/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
          'apikey': anonKey,
        },
        body: JSON.stringify({
          amount: total,
          productName: product.name,
          successUrl: `${window.location.origin}?payment=success`,
          cancelUrl: `${window.location.origin}?payment=cancelled`,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
        return;
      } else {
        throw new Error(data.error || 'Payment session could not be created. Please try again.');
      }
    } catch (err) {
      console.error('Order error:', err);
      setError(err.message || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="order-success">
        <div className="success-icon">✓</div>
        <h2>Order Placed Successfully!</h2>
        <p>Thank you for your order. We'll send you a confirmation email shortly.</p>
        <p>Order Total: <strong>£{calculateTotal()}</strong></p>
      </div>
    );
  }

  return (
    <div className="order-placement">
      <div className="order-header">
        <button className="back-button" onClick={onBack}>
          ← Back
        </button>
        <h2>{isKitOrder ? 'Complete Your Kit Order' : 'Complete Your Order'}</h2>
      </div>

      <div className="order-content">
        {/* Order Summary */}
        <div className="order-summary-card">
          <h3>Order Summary</h3>
          {isKitOrder && (
            <p className="order-kit-hint" style={{ marginBottom: '0.75rem', fontSize: '0.9rem', opacity: 0.9 }}>
              Preview shows front, back, and both sleeves in one sheet. Full design data is stored with
              your order. Add shipping details below.
            </p>
          )}
          <div className="product-preview">
            <img
              src={previewImage}
              alt={product.name}
              style={isKitOrder ? { maxHeight: 420, objectFit: 'contain', width: '100%' } : undefined}
            />
            <div>
              <h4>{product.name}</h4>
              <p className="price">{product.price}</p>
            </div>
          </div>

          <div className="price-breakdown">
            <div className="price-item">
              <span>Base Price:</span>
              <span>{product.price}</span>
            </div>
            {isKitOrder && kitDesign?.kitOptions?.fullSleeves && (
              <div className="price-item">
                <span>Full Sleeves:</span>
                <span>+ £2.00</span>
              </div>
            )}
            <div className="price-item">
              <span>Quantity:</span>
              <span>x{formData.quantity}</span>
            </div>
            {appliedCoupon && (
              <div className="price-item" style={{ color: '#22c55e' }}>
                <span>Coupon ({appliedCoupon.code}):</span>
                <span>-{appliedCoupon.discount_percent}%</span>
              </div>
            )}
            <div className="price-item total">
              <span>Total:</span>
              <span>£{calculateTotal()}</span>
            </div>
          </div>

          {/* Coupon Section */}
          <div className="coupon-section">
            <div className="coupon-input-row">
              <input
                type="text"
                placeholder="Discount code"
                value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value); setAppliedCoupon(null); setCouponError(''); }}
                className="coupon-input"
              />
              <button
                type="button"
                className="coupon-apply-btn"
                onClick={applyCoupon}
                disabled={couponLoading || !couponCode.trim()}
              >
                {couponLoading ? '...' : 'Apply'}
              </button>
            </div>
            {couponError && <p className="coupon-error">{couponError}</p>}
            {appliedCoupon && (
              <p className="coupon-success">
                ✅ {appliedCoupon.discount_percent}% discount applied!
              </p>
            )}
          </div>
        </div>

        {/* Order Form */}
        <div className="order-form-card">
          <h3>Shipping Information</h3>
          
          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                placeholder="John Doe"
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@example.com"
              />
            </div>

            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+44 7700 900000"
              />
            </div>

            <div className="form-group">
              <label>Shipping Address *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                placeholder="12 High Street"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  placeholder="London"
                />
              </div>

              <div className="form-group">
                <label>Postcode *</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  required
                  placeholder="SW1A 1AA"
                />
              </div>
            </div>

            {isShoes ? (
              <div className="form-row">
                <div className="form-group">
                  <label>UK shoe size *</label>
                  <select
                    name="size"
                    value={formData.size}
                    onChange={handleChange}
                    required
                  >
                    {ukShoeSizeOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <p className="order-size-hint">UK sizing as configured for this product.</p>
                </div>

                <div className="form-group">
                  <label>Quantity *</label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    min="1"
                    max="100"
                    required
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Size Selection *</label>
                    <div className="size-option">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="sizeType"
                          checked={!formData.useCustomSize}
                          onChange={() => setFormData({ ...formData, useCustomSize: false })}
                        />
                        <span>Standard Size</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="sizeType"
                          checked={formData.useCustomSize}
                          onChange={() => setFormData({ ...formData, useCustomSize: true })}
                        />
                        <span>Custom Measurements</span>
                      </label>
                    </div>
                  </div>
                </div>

                {!formData.useCustomSize ? (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Standard Size *</label>
                      <select
                        name="size"
                        value={formData.size}
                        onChange={handleChange}
                        required
                      >
                        {apparelSizeOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Quantity *</label>
                      <input
                        type="number"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleChange}
                        min="1"
                        max="100"
                        required
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="custom-size-section">
                      <h4>Custom Measurements (in inches)</h4>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Chest Width *</label>
                          <input
                            type="number"
                            name="chest"
                            value={formData.customSize.chest}
                            onChange={handleCustomSizeChange}
                            placeholder="e.g., 38"
                            step="0.5"
                            required={formData.useCustomSize}
                          />
                        </div>
                        <div className="form-group">
                          <label>Length *</label>
                          <input
                            type="number"
                            name="length"
                            value={formData.customSize.length}
                            onChange={handleCustomSizeChange}
                            placeholder="e.g., 28"
                            step="0.5"
                            required={formData.useCustomSize}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Shoulder Width *</label>
                          <input
                            type="number"
                            name="shoulder"
                            value={formData.customSize.shoulder}
                            onChange={handleCustomSizeChange}
                            placeholder="e.g., 18"
                            step="0.5"
                            required={formData.useCustomSize}
                          />
                        </div>
                        <div className="form-group">
                          <label>Sleeve Length *</label>
                          <input
                            type="number"
                            name="sleeve"
                            value={formData.customSize.sleeve}
                            onChange={handleCustomSizeChange}
                            placeholder="e.g., 9"
                            step="0.5"
                            required={formData.useCustomSize}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Quantity *</label>
                      <input
                        type="number"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleChange}
                        min="1"
                        max="100"
                        required
                      />
                    </div>
                  </>
                )}
              </>
            )}

            <div className="form-group">
              <label>Order Description / Special Instructions</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                placeholder="Add any special instructions or notes about your order..."
              />
            </div>

            <button 
              type="submit" 
              className="submit-order-button"
              disabled={loading}
            >
              {loading ? 'Placing Order...' : `Place Order - £${calculateTotal()}`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrderPlacement;
